import Image from 'next/image';
import NextLink from 'next/link';

import { Box, Button, Flex, LinkBox, LinkOverlay } from '@chakra-ui/react';

import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { RocketIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import {
  type Summer2026SaleVariant,
  getSummer2026SaleCheckoutHref,
  SUMMER_2026_SALE_CTA_LABEL,
  SUMMER_2026_SALE_VARIANTS,
} from '~/features/promotions/constants/summer-2026-sale';

export type SitewideSaleBannerProps = {
  variant: Summer2026SaleVariant;
  /** Live Braavos coupon from the promotion response; appended to the CTA as `?coupon=`. */
  promoCode?: string | null;
  /** Eager-load the art when the banner is the first/visible carousel slide. */
  imagePriority?: boolean;
  onCtaClick?: () => void;
};

function SitewideSaleBackground({
  imagePriority,
  variant,
}: {
  imagePriority?: boolean;
  variant: Summer2026SaleVariant;
}) {
  const config = SUMMER_2026_SALE_VARIANTS[variant];

  const catalogBackgroundImage = (
    <Image
      alt=''
      loading='eager'
      priority={imagePriority}
      sizes='656px'
      src={config.catalogBg}
      style={{ objectFit: 'cover', objectPosition: 'bottom', pointerEvents: 'none' }}
      fill
    />
  );

  return (
    <Box
      backgroundColor={config.backgroundColor}
      bottom={0}
      left={0}
      pointerEvents='none'
      position='absolute'
      right={0}
      top={0}
    >
      {config.catalogBgMobile ? (
        <>
          <Box bottom={0} display={{ base: 'block', md: 'none' }} left={0} position='absolute' right={0} top={0}>
            <Image
              alt=''
              loading='eager'
              priority={imagePriority}
              sizes='100vw'
              src={config.catalogBgMobile}
              style={{
                objectFit: config.catalogMobileObjectFit ?? 'cover',
                objectPosition: config.catalogMobileObjectPosition ?? 'bottom',
                pointerEvents: 'none',
              }}
              fill
            />
          </Box>
          <Box bottom={0} display={{ base: 'none', md: 'block' }} left={0} position='absolute' right={0} top={0}>
            {catalogBackgroundImage}
          </Box>
        </>
      ) : (
        <Box inset={0} position='absolute'>
          {catalogBackgroundImage}
        </Box>
      )}
    </Box>
  );
}

function SitewideSaleCta({ variant }: { variant: Summer2026SaleVariant }) {
  const config = SUMMER_2026_SALE_VARIANTS[variant];

  return (
    <Button
      alignItems='center'
      as='span'
      backgroundColor={config.homepageButtonHasRocketIcon ? 'blue.500' : config.ctaColor}
      borderRadius='4px'
      color='white'
      display='inline-flex'
      flexShrink={0}
      fontSize='12px'
      fontWeight='400'
      height='auto'
      lineHeight={1.2}
      paddingX='14px'
      paddingY='10px'
      pointerEvents='none'
      tabIndex={-1}
      variant='unstyled'
      leftIcon={
        config.homepageButtonHasRocketIcon ? <RocketIcon boxSize='16px' color='white' aria-hidden /> : undefined
      }
      aria-hidden
    >
      {SUMMER_2026_SALE_CTA_LABEL}
    </Button>
  );
}

/**
 * Catalog sitewide sale banner — background image + headline row + CTA.
 * The homepage surface uses `SitewideSalePromoCard` (`PromoCard`); `/experience` uses `ExperienceAtfHeroCard`.
 */
export function SitewideSaleBanner({ variant, promoCode, imagePriority, onCtaClick }: SitewideSaleBannerProps) {
  const config = SUMMER_2026_SALE_VARIANTS[variant];
  const ariaLabel = `${config.headlineLine1} ${config.headlineLine2}: ${SUMMER_2026_SALE_CTA_LABEL}`;

  return (
    <LinkBox
      borderRadius='8px'
      height='100%'
      isolation='isolate'
      minHeight={{ base: 'auto', md: '120px' }}
      overflow='hidden'
      position='relative'
      width='100%'
      {...(config.catalogBorderGradient
        ? {
            sx: {
              background: `linear-gradient(${config.backgroundColor}, ${config.backgroundColor}) padding-box, ${config.catalogBorderGradient} border-box`,
              border: '1px solid transparent',
            },
          }
        : {
            backgroundColor: config.backgroundColor,
            border: '1px solid',
            borderColor: config.catalogBorderColor,
          })}
    >
      <SitewideSaleBackground imagePriority={imagePriority} variant={variant} />
      <Flex
        alignItems={{ base: 'flex-start', md: 'flex-end' }}
        flexDirection={{ base: 'column', md: 'row' }}
        gap={{ base: 3, md: 4 }}
        height='100%'
        justifyContent='space-between'
        minHeight={{ base: 'auto', md: '120px' }}
        padding={{ base: '20px', md: '32px' }}
        position='relative'
        zIndex={1}
      >
        <Box maxWidth={{ md: '374px' }} minWidth={0}>
          <HeadingWithEmphasis
            text={`${config.headlineLine1}\n**${config.headlineLine2}**`}
            emphasisProps={{
              color: config.headlineAccentColor,
              fontWeight: '600',
              lineHeight: 1.15,
            }}
            headingProps={{
              as: 'h3',
              color: config.homepageTitleColor,
              fontSize: { base: '1.25rem', md: '1.5rem' },
              fontWeight: '600',
              letterSpacing: '-0.01em',
              lineHeight: 1.15,
              whiteSpace: 'pre-wrap',
            }}
          />
        </Box>
        <SitewideSaleCta variant={variant} />
      </Flex>
      <LinkOverlay
        aria-label={ariaLabel}
        as={NextLink}
        href={getSummer2026SaleCheckoutHref(variant, promoCode)}
        inset={0}
        position='absolute'
        zIndex={2}
        onClick={onCtaClick}
      />
    </LinkBox>
  );
}
